import { Fragment, type ReactNode } from "react";
import { graphql, readInlineData, useLazyLoadQuery } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { Comment } from "scmp-app/components/comment";
import { HundredTopTablesDiscoveryWidget } from "scmp-app/components/hundred-top-tables-discovery-widget";
import { Multimedia } from "scmp-app/components/multimedia";
import { PostMagazineDiscoveryWidget } from "scmp-app/components/post-magazine/discovery-widget";
import { ChinaFutureTechDiscoveryWidget } from "scmp-app/components/rhs-module/china-future-tech-discovery-widget";
import { OpenQuestionsSeriesDiscoveryWidget } from "scmp-app/components/rhs-module/open-questions-series-discovery-widget";
import { TrendingTopic } from "scmp-app/components/trending-topic";
import type { hooksSubSectionParentSectionTrendingTopicQuery } from "scmp-app/queries/__generated__/hooksSubSectionParentSectionTrendingTopicQuery.graphql";
import type { hooksSubSectionWidgetAndAdsQuery$key } from "scmp-app/queries/__generated__/hooksSubSectionWidgetAndAdsQuery.graphql";

import { getSubSectionAdsConfigs, getSubSectionWidgets } from "./helpers";
import {
  CommentContainer,
  FirstAds,
  FourthAds,
  MultimediaContainer,
  RHSModule1,
  RHSModule2,
  RHSModule3,
  RHSModule4,
  SecondAds,
  StyledHomeFocusArticle,
  ThirdAds,
  TrendingTopicContainer,
} from "./styles";
import type { SubSectionWidget } from "./types";

export const useSubSectionAndAdsWidgets = (
  reference: hooksSubSectionWidgetAndAdsQuery$key,
  isShowAdditionWidget: boolean,
) => {
  const data = readInlineData(
    graphql`
      fragment hooksSubSectionWidgetAndAdsQuery on Query
      @inline
      @argumentDefinitions(
        commentQueueName: { type: "String!" }
        entityId: { type: "String!" }
        focusQueueName: { type: "String!" }
        multimediaQueueName: { type: "String!" }
        parentSectionUuid: { type: "String" }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
        includeComment: { type: "Boolean!" }
        includeChinaFutureTechDiscovery: { type: "Boolean!" }
        includeFocusArticle: { type: "Boolean!" }
        includeMultimedia: { type: "Boolean!" }
        includePostMagazine: { type: "Boolean!" }
      ) {
        parentSection: section(filter: { entityUuid: $parentSectionUuid }) {
          entityId
        }
        ...commentQueueQuery
          @include(if: $includeComment)
          @arguments(
            commentLimit: 6
            commentQueueName: $commentQueueName
            scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
            withHarrysView: false
          )
        ...discoveryWidgetPostMagazineQueueQuery @include(if: $includePostMagazine)
        ...hundredTopTablesDiscoveryWidgetQuery
        ...multimediaQuery
          @include(if: $includeMultimedia)
          @arguments(multimediaQueueName: $multimediaQueueName)
        ...chinaFutureTechDiscoveryWidgetQuery @include(if: $includeChinaFutureTechDiscovery)
        ...openQuestionsSeriesDiscoveryWidgetQuery
          @arguments(scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds)
        subSection: section(filter: { entityId: $entityId }) {
          entityId
          advertZone(version: 2)
        }
        ...focusArticleHomeFocusArticleQuery
          @include(if: $includeFocusArticle)
          @arguments(focusQueueName: $focusQueueName)
      }
    `,
    reference,
  );

  // Since the trending topic of sub section pages need to use the queue from parent topic instead
  const trendingTopicData = useLazyLoadQuery<hooksSubSectionParentSectionTrendingTopicQuery>(
    graphql`
      query hooksSubSectionParentSectionTrendingTopicQuery($trendingTopicsQueueName: String!) {
        ...trendingTopicTopic @arguments(trendingTopicsQueueName: $trendingTopicsQueueName)
      }
    `,
    {
      trendingTopicsQueueName: `related_topics_${data.parentSection.entityId}`,
    },
    { fetchPolicy: "store-or-network" },
  );

  const adsConfigs = getSubSectionAdsConfigs(
    data.subSection.entityId,
    data?.subSection?.advertZone ?? undefined,
  );

  const renderComment = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "opinion",
        pgtype: "section",
      }}
    >
      <CommentContainer>
        <Comment reference={data} withMoreCommentButton={false} />
      </CommentContainer>
    </BaseLinkContextProvider>
  );
  const renderOpenQuestionsSeriesDiscoveryWidget = () => (
    <OpenQuestionsSeriesDiscoveryWidget reference={data} />
  );
  const renderMultimedia = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "multimedia",
        pgtype: "section",
      }}
    >
      <MultimediaContainer>
        <Multimedia reference={data} />
      </MultimediaContainer>
    </BaseLinkContextProvider>
  );

  const renderTrendingTopics = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "trending_topics",
        pgtype: "section",
      }}
    >
      <TrendingTopicContainer>
        <TrendingTopic reference={trendingTopicData} />
      </TrendingTopicContainer>
    </BaseLinkContextProvider>
  );
  const renderPostMagazine = () => <PostMagazineDiscoveryWidget reference={data} />;
  const renderFocusArticle = () => (
    <StyledHomeFocusArticle pageType="subsection" reference={data} />
  );
  const render100TopTables = () => <HundredTopTablesDiscoveryWidget reference={data} />;
  const renderChinaFutureTech = () => <ChinaFutureTechDiscoveryWidget reference={data} />;

  const rhsModuleComponents = [RHSModule1, RHSModule2, RHSModule3, RHSModule4];
  const adsComponentMap = [FirstAds, SecondAds, ThirdAds, FourthAds];

  const sectionWidgets = getSubSectionWidgets(data.subSection.entityId);
  const sectionWidgetComponentMap: Record<SubSectionWidget, () => ReactNode> = {
    "100-top-tables": render100TopTables,
    "china-future-tech-discovery": renderChinaFutureTech,
    comment: renderComment,
    "focus-article": renderFocusArticle,
    multimedia: renderMultimedia,
    "open-questions-series-discovery": renderOpenQuestionsSeriesDiscoveryWidget,
    "post-magazine": renderPostMagazine,
    "trending-topics": renderTrendingTopics,
  };
  const rhsModule = sectionWidgets.map(widget => sectionWidgetComponentMap[widget]);
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const render = () => {
    const shouldRenderAdditionalWidgets =
      !isShowAdditionWidget && sectionWidgets.length > 3 && isDesktopUp;

    const widgets = rhsModule.map((rhsModule, index) => {
      const Wrapper = rhsModuleComponents[index];
      if (index > 1 && shouldRenderAdditionalWidgets) return null;

      return (
        <Fragment key={index}>
          <Wrapper>{rhsModule()}</Wrapper>
        </Fragment>
      );
    });

    const ads = adsConfigs.map((adsConfig, index) => {
      const ComponentType = adsComponentMap[index];
      if (index > 2 && shouldRenderAdditionalWidgets) return null;
      return (
        <ComponentType key={`AdSlot${index}`}>
          {adsConfig.map(
            (config, configIndex) => config && <AdSlot {...config} key={configIndex} />,
          )}
        </ComponentType>
      );
    });

    return [...widgets, ...ads];
  };

  return { render };
};
